// src/components/History.js
import React, { useState } from "react";
import { History, CheckSquare, Edit3, Trash2, UserCheck, Search, Trello } from "lucide-react";
import { doc, deleteDoc, updateDoc } from "firebase/firestore";
import { PRODUCT_CONFIG } from "../data";
import getItemDetails from "../utils/getItemDetails";
// 引入 unarchiveTrelloCard
import { createTrelloCard, searchBoardCards, addTrelloComment, unarchiveTrelloCard } from "../services/trello";

const HistoryComponent = ({
  history,
  loadHistoryRecord,
  db,
  appId,
  IS_TEST_MODE,
  editingHistoryId,
}) => {
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const toggleSelection = (id) => {
    setSelectedRecordIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleDeleteHistory = async (id) => {
    if (!confirm("確定要刪除此紀錄嗎？")) return;
    if (!IS_TEST_MODE && db) {
      try {
        await deleteDoc(
          doc(db, "artifacts", appId, "public", "data", "quotes", id)
        );
      } catch (e) {
        console.error("Delete error", e);
        alert("刪除失敗");
      }
    }
  };

  // getItemDetails 已提取至 src/utils/getItemDetails.js

  const handleTrelloUpload = async (record) => {
    if (record.trelloUrl) {
       if(confirm(`此報價單已上傳過，要開啟 Trello 卡片嗎？\n(按取消則重新上傳/更新)`)) {
          window.open(record.trelloUrl, "_blank");
          return;
       }
    }

    if (isUploading) return;
    
    const productName = PRODUCT_CONFIG[record.productType]?.label || "商品";
    const sourceName = record.source || "無來源";
    const customer = record.customerName || "未知客戶";
    const shortId = record.id.substring(0, 6);
    const creatorEmail = record.createdBy?.email || "未知人員";
    
    if (!confirm(`準備將「${customer}」的報價單上傳至 Trello 嗎？`)) return;

    setIsUploading(true);
    try {
      const cardTitle = `${shortId} 客製${productName} ${sourceName} ${customer}`;

      let desc = `**詳細品項內容**:\n\n`;
      (record.items || []).forEach((item, idx) => {
        let displayName = item.styleName || item.cushionType;
        if (record.productType === "dust_mite") displayName = "除塵墊";
        const details = getItemDetails(item, record.productType);
        
        desc += `${idx + 1}. **${displayName}**`;
        if (item.width && item.length) desc += ` (${item.width}x${item.length}cm)`;
        desc += ` - ${item.quantity}件`;
        if (details) desc += `\n   規格: ${details}`;
        if (item.finalPrice) desc += `\n   金額: $${item.finalPrice}`;
        desc += `\n\n`;
      });
      desc += `\n---\n`; 
      desc += `經手人員: ${creatorEmail}\n`;
      desc += `上傳時間: ${new Date().toLocaleString()}`;

      let targetUrl = "";
      let targetCardId = "";

      // 搜尋 (雙軌搜尋：Open + Archived)
      const searchResults = await searchBoardCards(db, appId, customer);
      const existingCards = searchResults.filter(card => card.name.includes(customer));
      
      let useExisting = false;
      let targetExistingCard = null;
      
      if (existingCards.length === 1) {
        targetExistingCard = existingCards[0];
        const status = targetExistingCard.closed ? " (已封存)" : "";
        const userChoice = confirm(
            `🔎 找到 1 張同名卡片${status}：\n【 ${targetExistingCard.name} 】\n\n` +
            `● 按「確定」：合併 (若已封存將自動復原)\n` +
            `● 按「取消」：建立新卡片`
        );
        useExisting = userChoice;
        if (!useExisting) targetExistingCard = null;

      } else if (existingCards.length > 1) {
        let msg = `🔎 找到 ${existingCards.length} 張同名卡片，請選擇要合併的對象：\n\n`;
        existingCards.forEach((c, i) => {
            const status = c.closed ? " (已封存)" : "";
            msg += `${i + 1}. ${c.name}${status}\n`;
        });
        msg += `\n請輸入編號 (1 ~ ${existingCards.length}) 進行合併\n輸入 0 或取消：建立新卡片`;

        const input = prompt(msg, "0");
        const choiceIndex = parseInt(input) - 1;

        if (input && !isNaN(choiceIndex) && choiceIndex >= 0 && choiceIndex < existingCards.length) {
            targetExistingCard = existingCards[choiceIndex];
            useExisting = true;
        } else {
            useExisting = false;
            targetExistingCard = null;
        }
      }

      if (useExisting && targetExistingCard) {
        // --- 合併模式 ---
        
        // 1. 如果卡片是封存狀態，先把它救回來 (Unarchive)
        if (targetExistingCard.closed) {
            await unarchiveTrelloCard(db, appId, targetExistingCard.id);
        }

        // 2. 新增留言
        await addTrelloComment(db, appId, targetExistingCard.id, `**[追加報價單]**\n${cardTitle}\n\n${desc}`);
        targetUrl = targetExistingCard.shortUrl;
        targetCardId = targetExistingCard.id;
        
        if (targetExistingCard.closed) {
             alert(`✅ 已合併並「解封存」卡片：「${targetExistingCard.name}」！`);
        } else {
             alert(`✅ 已合併至「${targetExistingCard.name}」！`);
        }

      } else {
        // --- 新增模式 ---
        const newCard = await createTrelloCard(db, appId, cardTitle, desc);
        targetUrl = newCard.shortUrl;
        targetCardId = newCard.id;
        
        if (existingCards.length > 0) {
            alert("✅ 已建立新卡片！(未選擇合併)");
        } else {
            alert("✅ 已建立全新的 Trello 卡片！");
        }
      }

      if (!IS_TEST_MODE && db && targetUrl) {
         try {
            await updateDoc(doc(db, "artifacts", appId, "public", "data", "quotes", record.id), {
                trelloUrl: targetUrl,
                trelloCardId: targetCardId,
                trelloUploadedAt: new Date().toISOString()
            });
         } catch(e) {
             console.error("回寫失敗", e);
         }
      }
      
      if (targetUrl) window.open(targetUrl, "_blank");

    } catch (error) {
      console.error(error);
      alert("❌ 上傳失敗：" + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const filteredHistory = history.filter((record) =>
    (record.customerName || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-700">
          歷史報價{" "}
          <span className="ml-2 text-xs font-normal text-slate-500">
            (共 {filteredHistory.length} 筆)
          </span>
        </h2>
        
        <div className="relative w-full md:w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
            <Search size={16} />
          </div>
          <input
            type="text"
            placeholder="搜尋客戶姓名..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all"
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm("")}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
            >
              <span className="text-xs bg-slate-200 rounded-full px-1.5 py-0.5">✕</span>
            </button>
          )}
        </div>
      </div>
      
      {filteredHistory.length === 0 ? (
        <div className="p-12 text-center text-slate-400">
          {searchTerm ? (
            <>
              <Search size={48} className="mx-auto mb-3 opacity-20" />
              <p>找不到符合 "{searchTerm}" 的紀錄</p>
            </>
          ) : (
            <>
              <History size={48} className="mx-auto mb-3 opacity-20" />
              <p>尚無紀錄</p>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 font-medium">
              <tr>
                <th className="p-4 w-12 text-center"></th>
                <th className="p-4">客戶/日期</th>
                <th className="p-4">內容摘要</th>
                <th className="p-4 text-right">總金額</th>
                <th className="p-4 text-center">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredHistory.map((record) => {
                const isSelected = selectedRecordIds.includes(record.id);
                const isEditing = editingHistoryId === record.id;
                const hasTrello = !!record.trelloUrl; 

                return (
                  <tr
                    key={record.id}
                    className={`transition-colors ${
                      isSelected
                        ? "bg-amber-50"
                        : isEditing
                        ? "bg-blue-50/30"
                        : "hover:bg-slate-50/50"
                    }`}
                  >
                    <td className="p-4 text-center align-top">
                      <div
                        onClick={() => toggleSelection(record.id)}
                        className={`p-1 rounded cursor-pointer transition-all ${
                          isSelected
                            ? "bg-amber-500 text-white shadow-sm scale-110"
                            : "text-slate-300 hover:bg-slate-100 hover:text-slate-400"
                        }`}
                      >
                        <CheckSquare size={20} />
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {record.customerName || "未知客戶"}
                        {record.source && (
                          <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200">
                            {record.source}
                          </span>
                        )}
                        {isEditing && (
                          <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">
                            編輯中
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {record.date}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="space-y-1">
                        {(record.items || []).map((item, idx) => {
                          let displayName = item.styleName || item.cushionType;
                          if (record.productType === "dust_mite")
                            displayName = "除塵墊";

                          const detailStr = getItemDetails(
                            item,
                            record.productType
                          );

                          return (
                            <div
                              key={idx}
                              className="text-slate-500 text-xs flex flex-wrap gap-1 items-center"
                            >
                              <span className="font-bold text-slate-700">
                                {displayName}
                                {record.productType === "apron" &&
                                  item.details?.unitPrice && (
                                    <span className="text-slate-400 font-normal ml-1">
                                      ($
                                      {Math.round(
                                        item.details.unitPrice *
                                          (item.details.discountRate || 1)
                                      )}
                                      )
                                    </span>
                                  )}
                              </span>
                              {detailStr && (
                                <span className="text-slate-600">
                                  {detailStr}
                                </span>
                              )}
                              <span>
                                {item.width
                                  ? `${item.width}x${item.length}`
                                  : ""}
                              </span>
                              <span>({item.quantity}件)</span>
                              <span className="text-slate-400 ml-1">
                                {record.productType === "apron"
                                  ? `小計:$${item.finalPrice}`
                                  : `(¥${(
                                      Number(item.details?.rmbProductCost || 0) +
                                      Number(item.details?.rmbLabelFee || 0)
                                    ).toFixed(2)} | 台幣:$${
                                      item.details?.twdImport ||
                                      item.twdBaseSellingPrice
                                    })`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="p-4 text-right align-top">
                      <div className="font-bold text-slate-900 text-lg">
                        ${(record.totalAmount || 0).toLocaleString()}
                      </div>
                    </td>
                    <td className="p-4 text-center align-top">
                      <div className="flex items-center justify-center gap-1 mb-2">
                        <button
                          onClick={() => loadHistoryRecord(record)}
                          className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="編輯"
                        >
                          <Edit3 size={16} />
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTrelloUpload(record);
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${
                              hasTrello 
                                ? "text-green-600 bg-green-50 hover:bg-green-100" 
                                : "text-blue-600 hover:bg-blue-50"
                          }`}
                          title={hasTrello ? "開啟 Trello 卡片" : "上傳至 Trello"}
                        >
                          <Trello size={16} />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteHistory(record.id);
                          }}
                          className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                          title="刪除"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      {record.createdBy?.email && (
                        <div className="text-[10px] text-slate-400 flex items-center justify-center gap-1">
                          <UserCheck size={10} />{" "}
                          {(record.createdBy.email || "").split("@")[0]}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HistoryComponent;